import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

/**
 * Hook pour sécuriser le parcours de commande
 * Vérifie que l'organizationId reste cohérent tout au long du processus
 */
export const useOrderJourneySecurity = () => {
  const { idorga } = useParams<{ idorga: string }>();
  const navigate = useNavigate();
  // Initialiser directement avec idorga si disponible pour éviter le décalage au premier rendu
  const [initialOrganizationId, setInitialOrganizationId] = useState<
    string | null
  >(idorga || null);
  const [isSecurityCheckPassed, setIsSecurityCheckPassed] = useState(false);

  // Stocker l'organizationId initial au premier chargement (si pas déjà défini)
  useEffect(() => {
    if (idorga && !initialOrganizationId) {
      setInitialOrganizationId(idorga);
      setIsSecurityCheckPassed(true);
      console.log(
        "🔒 Sécurité: OrganizationId initial défini:",
        idorga,
      );
    } else if (idorga && initialOrganizationId === idorga) {
      // S'assurer que isSecurityCheckPassed est true si les IDs correspondent
      setIsSecurityCheckPassed(true);
    }
  }, [idorga, initialOrganizationId]);

  // Vérifier la cohérence de l'organizationId à chaque changement
  useEffect(() => {
    if (
      initialOrganizationId &&
      idorga &&
      idorga !== initialOrganizationId
    ) {
      console.warn("🚨 Sécurité: Changement d'organisation détecté!");
      console.warn("   Initial:", initialOrganizationId);
      console.warn("   Actuel:", idorga);

      // Afficher un message d'erreur
      toast.error(
        "Changement d'organisation détecté. Redirection vers le début du processus.",
      );

      // Rediriger vers la première étape avec la nouvelle organisation
      setTimeout(() => {
        navigate(`/orga/${idorga}/order`, { replace: true });
      }, 2000);

      setIsSecurityCheckPassed(false);
    } else if (
      idorga &&
      initialOrganizationId &&
      idorga === initialOrganizationId
    ) {
      setIsSecurityCheckPassed(true);
    }
  }, [idorga, initialOrganizationId, navigate]);

  // Fonction pour vérifier manuellement la sécurité
  const checkSecurity = (): boolean => {
    if (!idorga || !initialOrganizationId) {
      return false;
    }

    if (idorga !== initialOrganizationId) {
      console.warn(
        "🚨 Sécurité: Vérification échouée - organizationId incohérent",
      );
      return false;
    }

    return true;
  };

  // Fonction pour forcer la redirection si nécessaire
  const forceRedirectToStart = () => {
    if (idorga) {
      console.log("🔄 Sécurité: Redirection forcée vers le début du processus");
      navigate(`/orga/${idorga}/order`, { replace: true });
    }
  };

  // Ne considérer qu'un changement réel (pas juste l'état initial où initialOrganizationId est null)
  const hasOrganizationChanged = initialOrganizationId !== null && idorga !== null && idorga !== initialOrganizationId;
  
  // Log pour déboguer
  if (hasOrganizationChanged) {
    console.warn('🚨 hasOrganizationChanged = true:', {
      idorga,
      initialOrganizationId,
      hasOrganizationChanged
    });
  }

  return {
    organizationId: idorga,
    initialOrganizationId,
    isSecurityCheckPassed,
    checkSecurity,
    forceRedirectToStart,
    hasOrganizationChanged,
  };
};
